import '@theme/quick-add';
import '@theme/product-form';

/**
 * Cached recommendation markup, keyed by request URL, so re-rendering the cart
 * drawer (which happens on every cart change) does not refetch the same list.
 * @type {Map<string, string>}
 */
const cache = new Map();

/**
 * Renders "You may also like" recommendations inside the cart drawer.
 *
 * The markup comes from `sections/cart-drawer-upsell.liquid`, fetched through
 * the Section Rendering API on the product recommendations route. Products that
 * are already in the cart are dropped; the rest are paged `data-max-items` at a
 * time behind the next button, wrapping back to the first page at the end.
 *
 * Every cart change re-renders the drawer section, and the morph replaces this
 * element's children with the empty server-rendered placeholder again (`hidden`,
 * no `data-loaded`). `connectedCallback` does not re-fire for an in-place morph,
 * so a MutationObserver watches for that reset and reloads — same approach the
 * theme's own `product-recommendations.js` uses.
 */
class CartUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #loading = false;

  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {number} */
  #pageSize = 2;

  /** @type {number} */
  #offset = 0;

  #mutationObserver = new MutationObserver(() => {
    if (this.dataset.loaded === 'true' || this.#loading) return;
    this.#load();
  });

  connectedCallback() {
    this.#mutationObserver.observe(this, { attributes: true, childList: true });
    if (this.dataset.loaded === 'true') return;
    this.#load();
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#loading = false;
  }

  async #load() {
    const { url, sectionId, productId } = this.dataset;
    if (!url || !sectionId || !productId) return;

    const endpoint = `${url}&product_id=${productId}&section_id=${sectionId}`;
    this.#loading = true;

    try {
      let html = cache.get(endpoint);

      if (html == null) {
        this.#activeFetch?.abort();
        this.#activeFetch = new AbortController();
        const response = await fetch(endpoint, { signal: this.#activeFetch.signal });
        if (!response.ok) return;
        html = await response.text();
        cache.set(endpoint, html);
      }

      const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-upsell-content]');
      if (!content) return;

      const inCart = new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));

      for (const item of content.querySelectorAll('.cart-upsell__item')) {
        if (inCart.has(item.dataset.productId || '')) item.remove();
      }

      if (!content.querySelector('.cart-upsell__item')) return;

      this.replaceChildren(content);
      this.#items = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.cart-upsell__item')]);
      this.#pageSize = Math.max(1, Number.parseInt(this.dataset.maxItems || '3', 10));
      this.#offset = 0;

      const next = this.querySelector('[data-cart-upsell-next]');
      if (next instanceof HTMLElement) {
        next.addEventListener('click', () => {
          this.#offset = (this.#offset + this.#pageSize) % this.#items.length;
          this.#renderPage();
        });
      }

      // Zurück blättert um dieselbe Seitenlänge. Das Modulo hält den Wert im
      // Bereich, auch wenn der Knopf einmal sichtbar bleibt, wo er es nicht
      // sein sollte — ein negativer Versatz versteckte sonst ALLE Kacheln.
      const prev = this.querySelector('[data-cart-upsell-prev]');
      if (prev instanceof HTMLElement) {
        prev.addEventListener('click', () => {
          const total = this.#items.length;
          this.#offset = (this.#offset - this.#pageSize + total) % total;
          this.#renderPage();
        });
      }

      this.#renderPage();
      this.dataset.loaded = 'true';
      this.hidden = false;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.warn('[cart-upsell] Could not load recommendations:', error);
    } finally {
      this.#activeFetch = null;
      this.#loading = false;
    }
  }

  /**
   * Shows the `#pageSize` items starting at `#offset`, hiding the rest.
   * Toggling `hidden` on descendants does not trip the MutationObserver, which
   * only watches this element's own attributes and child list.
   */
  #renderPage() {
    const total = this.#items.length;
    for (const [index, item] of this.#items.entries()) {
      const position = (index - this.#offset + total) % total;
      item.hidden = position >= this.#pageSize;
    }

    // Die Knöpfe gehören zum Zustand der Seite und werden deshalb HIER
    // gesetzt, nicht einmalig beim Aufbau: nach jedem Blättern stimmt sonst
    // einer von beiden nicht mehr.
    const mehrereSeiten = total > this.#pageSize;
    const next = this.querySelector('[data-cart-upsell-next]');
    if (next instanceof HTMLElement) next.hidden = !mehrereSeiten;
    const prev = this.querySelector('[data-cart-upsell-prev]');
    if (prev instanceof HTMLElement) prev.hidden = !mehrereSeiten || this.#offset === 0;
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
